import { addWeeks, format, startOfMonth } from 'date-fns'

import { Consultation, ConsultationActionType, ConsultationStatus, Role, StrapiFileInfo } from '../../@types'
import { Config } from '../../context/config'
import { Action, OrderConsultation, PatientConsultation } from '../../data/use-patient-current-consultation/types'

const sortActions = (actions: Action[]) =>
  actions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

const isClosedStatus = (status: ConsultationStatus | ConsultationActionType) =>
  status === ConsultationStatus.CLOSED ||
  status === ConsultationStatus.CLOSED_WITH_ERROR ||
  status === ConsultationStatus.CLOSING ||
  status === ConsultationStatus.CANCELLED ||
  status === ConsultationStatus.CANCELLING

export const groupConsultationsByMonth = (consultations: Consultation[]) => {
  return consultations.reduce((acc, consultation) => {
    const group = isClosedStatus(consultation.status) ? 'past' : 'upcoming'
    const createdAtDate = new Date(consultation.startTime ?? consultation.createdAt)
    // We set the date in the middle of the month to avoid month key discrepancies when doing new Date
    const key = format(addWeeks(startOfMonth(createdAtDate), 2), 'yyyy-MM')

    if (!acc[group]) acc[group] = {}
    if (!acc[group][key]) acc[group][key] = []
    acc[group][key].push(consultation)

    return acc
  }, {} as Record<string, Record<string, Consultation[]>>)
}

export const getClosedAction = (consultation: Consultation) => {
  return consultation.actions.find(action => isClosedStatus(action.type))
}

const orderByClosedAtDesc = (a: Consultation, b: Consultation) => {
  const aClosedAt = getClosedAction(a)?.createdAt
  const bClosedAt = getClosedAction(b)?.createdAt

  if (!aClosedAt) return 1
  if (!bClosedAt) return -1
  return new Date(bClosedAt).getTime() - new Date(aClosedAt).getTime()
}

export const getLastClosedConsultation = (consultations: Consultation[]) => {
  return consultations
    .filter(consultation => hasConsultationTerminatedStatus(consultation))
    .sort(orderByClosedAtDesc)[0] // Array.at() isn't supported in Expo 45's version of Hermes
}

const orderStrapiFileByUploadedAtDesc = (a: StrapiFileInfo, b: StrapiFileInfo) => {
  if (!a.uploadedAt) return 1
  if (!b.uploadedAt) return -1
  return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
}

export const getLastSymptomsDocument = (consultation: Consultation) => {
  return consultation.symptomsDocuments?.sort(orderStrapiFileByUploadedAtDesc)[0]
}

export const hasConsultationTerminatedStatus = (consultation: Consultation | OrderConsultation) =>
  consultation.status === ConsultationStatus.CLOSED ||
  consultation.status === ConsultationStatus.CLOSED_WITH_ERROR ||
  consultation.status === ConsultationStatus.CANCELLED

export const isConsultationCancelled = (consultation: Consultation | OrderConsultation) =>
  consultation.status === ConsultationStatus.CANCELLED

export const isConsultationCancelledByPatient = (consultation: Consultation | OrderConsultation) =>
  consultation.actions.find(action => action.type === ConsultationActionType.CANCELLED)?.user.role === Role.PATIENT

export const isConsultationCancelledByClinician = (consultation: Consultation | OrderConsultation) => {
  const cancellationRole = consultation.actions.find(action => action.type === ConsultationActionType.CANCELLED)?.user
    .role

  if (!cancellationRole) return false

  return [Role.CLINICIAN, Role.CLINICIAN_AND_CLINICAL_MANAGER].includes(cancellationRole)
}

export const getLastCreatedConsultationAction = (consultation: OrderConsultation) => {
  return sortActions(consultation.actions)[0]
}

export const getMissedCalls = (consultation: OrderConsultation) => {
  // TODO: Check if this is the correct way to count missed calls
  return consultation?.actions.filter(a => a.type === ConsultationActionType.UNASSIGNED).length ?? 0
}

export const findLastFeedbackAction = (actions: Action[]) => {
  return sortActions(actions).find(action => action.type.includes('PHONE_CALL') || action.type.includes('VIDEO_CALL'))
}

export const isCallActionCompleted = (type: ConsultationActionType) =>
  type === ConsultationActionType.PHONE_CALL_COMPLETED || type === ConsultationActionType.VIDEO_CALL_COMPLETED

export const isCallActionCancelled = (type: ConsultationActionType) =>
  type === ConsultationActionType.PHONE_CALL_CANCELLED || type === ConsultationActionType.VIDEO_CALL_CANCELLED

export const canGetRefund = (consultation: PatientConsultation, config: Config): boolean => {
  return (
    new Date(Number(consultation?.startTime)).getTime() - Date.now() >
    config.minCancellationTimeInAdvanceMinutes * 60 * 1000
  )
}
